import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Modal, Form,
  Alert, InputGroup, Pagination, Spinner, Dropdown, OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  Receipt, Search, Eye, Download, Printer, Filter, Calendar,
  FileText, DollarSign, User, Phone, Building, Trash2,
  RefreshCw, Plus, Edit
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import { useTheme } from '../../contexts/ThemeContext';
import saleService from '../../services/saleService';
import InvoicePrintService from './InvoicePrintService';
import InvoiceExportButton from './InvoiceExportButton';

const InvoiceManagement = () => {
  const { user } = useAuth();
  const { currentBoutique } = useBoutique();
  const { primaryColor, secondaryColor, logoUrl } = useTheme();

  // États principaux
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ✅ États filtres — défaut "today" comme SalesList
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [statusFilter, setStatusFilter] = useState('');

  // ✅ États pagination serveur
  const [salesData, setSalesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const pageSizeOptions = [10, 15, 20, 30, 50];
  const [serverPagination, setServerPagination] = useState({
    totalPages: 1,
    totalElements: 0,
  });
  const [serverStats, setServerStats] = useState({
    filteredCount: 0,
    filteredRevenue: 0,
  });

  // ✅ Stats compteurs (pour les badges des filtres)
  const [filterCounts, setFilterCounts] = useState({});

  // Debounce recherche
  const [searchDebounce, setSearchDebounce] = useState(null);

  // États pour modals et actions
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Boutique data
  const getBoutiqueData = () => ({
    ...currentBoutique,
    primaryColor: primaryColor || currentBoutique?.primaryColor || currentBoutique?.primary_color || '#0d6efd',
    secondaryColor: secondaryColor || currentBoutique?.secondaryColor || currentBoutique?.secondary_color || '#6c757d',
    logoUrl: logoUrl || currentBoutique?.logoUrl || currentBoutique?.logo_url || null,
    name: currentBoutique?.name || 'SmartBoutik',
    address: currentBoutique?.address || 'Adresse non spécifiée',
    phone: currentBoutique?.phone || currentBoutique?.telephone || null,
    email: currentBoutique?.email || null
  });

  // ✅ Charger les factures depuis le serveur (paginé + filtré)
  const loadSales = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        dateFilter: dateFilter,
        status: statusFilter || undefined,
        search: searchTerm.trim() || undefined,
      };

      // Charger ventes paginées + stats en parallèle
      const [salesResponse, statsResponse] = await Promise.all([
        saleService.getSalesFiltered(page, itemsPerPage, filters),
        saleService.getSalesStatsAggregated(
          filters.dateFilter || 'today',
          null, null
        )
      ]);

      if (salesResponse.success) {
        setSalesData(salesResponse.data);
        if (salesResponse.pagination) {
          setServerPagination({
            totalPages: salesResponse.pagination.totalPages || 1,
            totalElements: salesResponse.pagination.totalElements || 0,
          });
        }
        setServerStats({
          filteredCount: salesResponse.filteredCount ?? 0,
          filteredRevenue: salesResponse.filteredRevenue ?? 0,
        });
      } else {
        setError(salesResponse.message || 'Erreur lors du chargement');
        setSalesData([]);
      }

      // Stats compteurs pour les badges
      if (statsResponse.success && statsResponse.data) {
        const fc = statsResponse.data.filterCounts || {};
        setFilterCounts({
          total: fc.total || 0,
          today: fc.today || 0,
          week: fc.week || 0,
          month: fc.month || 0,
        });
      }
    } catch (error) {
      console.error('❌ Erreur chargement factures:', error);
      setError('Erreur de connexion');
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Chargement initial
  useEffect(() => {
    loadSales(0);
  }, []);

  // ✅ Recharger quand un filtre change (sauf searchTerm qui utilise debounce)
  useEffect(() => {
    loadSales(0);
    setCurrentPage(1);
  }, [dateFilter, statusFilter, itemsPerPage]);

  // ✅ Debounce sur la recherche
  useEffect(() => {
    if (searchDebounce) clearTimeout(searchDebounce);
    const timer = setTimeout(() => {
      loadSales(0);
      setCurrentPage(1);
    }, 400);
    setSearchDebounce(timer);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Navigation pages
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= serverPagination.totalPages) {
      setCurrentPage(newPage);
      loadSales(newPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  // ✅ Helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F CFA';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (sale) => {
    if (sale.isCancelled) return <Badge bg="danger">Annulée</Badge>;
    if (sale.isCredit && !sale.isCreditPaid) return <Badge bg="warning">À crédit</Badge>;
    if (sale.isCredit && sale.isCreditPaid) return <Badge bg="success">Crédit soldé</Badge>;
    return <Badge bg="primary">Payé</Badge>;
  };

  const getPaymentBadge = (method) => {
    const map = {
      CASH: { label: 'Espèces', bg: 'success', icon: '💵' },
      WAVE: { label: 'Wave', bg: 'info', icon: '📱' },
      ORANGE_MONEY: { label: 'OM', bg: 'warning', icon: '🍊' },
      BANK_TRANSFER: { label: 'Virement', bg: 'primary', icon: '🏦' },
      CHECK: { label: 'Chèque', bg: 'secondary', icon: '📋' },
      CREDIT: { label: 'Crédit', bg: 'danger', icon: '💳' },
      CARD: { label: 'Carte', bg: 'dark', icon: '💳' },
      MIXED: { label: 'Mixte', bg: 'info', icon: '🔀' },
    };
    return map[method] || { label: method || 'N/A', bg: 'secondary', icon: '💰' };
  };

  const getDateFilterLabel = () => {
    const labels = { today: "Aujourd'hui", week: "Cette semaine", month: "Ce mois", all: "Toutes les dates" };
    return labels[dateFilter] || "Aujourd'hui";
  };

  // Actions factures
  const handleViewInvoice = (sale) => {
    setSelectedSale(sale);
    setShowInvoiceModal(true);
  };

  const handlePrintInvoice = async (sale) => {
    try {
      setPrinting(sale.id);
      const boutiqueData = getBoutiqueData();
      const result = await InvoicePrintService.printInvoice(sale, boutiqueData);
      if (result.success) {
        setSuccess(`Facture ${sale.saleNumber} imprimée avec succès`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || "Erreur lors de l'impression");
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('❌ Erreur impression:', error);
      setError("Erreur lors de l'impression");
      setTimeout(() => setError(null), 5000);
    } finally {
      setPrinting(null);
    }
  };

  const handleDownloadInvoice = async (sale) => {
    try {
      setDownloading(sale.id);
      const boutiqueData = getBoutiqueData();
      const result = await InvoicePrintService.downloadInvoicePDF(sale, boutiqueData);
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.message || 'Erreur lors du téléchargement');
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('❌ Erreur téléchargement:', error);
      setError('Erreur lors du téléchargement');
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewInvoice = (sale) => {
    const boutiqueData = getBoutiqueData();
    InvoicePrintService.previewInvoice(sale, boutiqueData);
  };

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
        <Col>
          <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'}`}>
            <div>
              <h2 className="mb-1" style={{ fontSize: isMobile ? '1.3rem' : '1.75rem' }}>
                <Receipt size={isMobile ? 22 : 28} className="me-2" />
                Gestion des Factures
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                {serverStats.filteredCount} facture(s) • CA: {formatCurrency(serverStats.filteredRevenue)}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size={isMobile ? 'sm' : 'md'}
                onClick={() => { loadSales(0); setCurrentPage(1); }}
                disabled={loading}
              >
                <RefreshCw size={16} className={`me-1 ${loading ? 'spin' : ''}`} />
                {isMobile ? '' : 'Actualiser'}
              </Button>
              <InvoiceExportButton
                activeFilters={{
                  dateFilter: dateFilter,
                  statusFilter: statusFilter,
                  searchTerm: searchTerm,
                }}
                boutiqueInfo={{
                  name: currentBoutique?.name,
                  address: currentBoutique?.address,
                  phone: currentBoutique?.phone || currentBoutique?.telephone,
                  email: currentBoutique?.email,
                }}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* ✅ Filtres — style SalesList */}
      <Card className="mb-3" style={{ overflow: 'visible', zIndex: 10, position: 'relative' }}>
        <Card.Body className={isMobile ? 'p-2' : 'p-3'} style={{ overflow: 'visible' }}>
          <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'gap-3 align-items-center flex-wrap'}`}>
            {/* Recherche */}
            <InputGroup style={{ maxWidth: isMobile ? '100%' : '300px' }}>
              <InputGroup.Text style={{ padding: '0.375rem 0.5rem' }}>
                <Search size={14} />
              </InputGroup.Text>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Client, N° facture, téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {/* Filtre date */}
            <Dropdown>
              <Dropdown.Toggle variant="outline-primary" size="sm" className="d-flex align-items-center">
                <Calendar size={14} className="me-1" />
                {getDateFilterLabel()}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ zIndex: 1050 }}>
                <Dropdown.Item active={dateFilter === 'today'} onClick={() => setDateFilter('today')}>
                  Aujourd'hui ({filterCounts.today || 0})
                </Dropdown.Item>
                <Dropdown.Item active={dateFilter === 'week'} onClick={() => setDateFilter('week')}>
                  Cette semaine ({filterCounts.week || 0})
                </Dropdown.Item>
                <Dropdown.Item active={dateFilter === 'month'} onClick={() => setDateFilter('month')}>
                  Ce mois ({filterCounts.month || 0})
                </Dropdown.Item>
                <Dropdown.Item active={dateFilter === 'all'} onClick={() => setDateFilter('all')}>
                  Toutes les dates ({filterCounts.total || 0})
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Filtre statut */}
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center">
                <Filter size={14} className="me-1" />
                {statusFilter === 'paid' ? 'Payées' : statusFilter === 'credit' ? 'À crédit' : statusFilter === 'creditPaid' ? 'Crédit soldé' : 'Tous statuts'}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ zIndex: 1050 }}>
                <Dropdown.Item active={statusFilter === ''} onClick={() => setStatusFilter('')}>
                  Tous les statuts
                </Dropdown.Item>
                <Dropdown.Item active={statusFilter === 'paid'} onClick={() => setStatusFilter('paid')}>
                  Payées
                </Dropdown.Item>
                <Dropdown.Item active={statusFilter === 'credit'} onClick={() => setStatusFilter('credit')}>
                  À crédit
                </Dropdown.Item>
                <Dropdown.Item active={statusFilter === 'creditPaid'} onClick={() => setStatusFilter('creditPaid')}>
                  Crédit soldé
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Badge résumé */}
            <Badge bg="light" text="dark" className="py-2 px-3" style={{ fontSize: '0.8rem' }}>
              {serverStats.filteredCount} facture(s)
            </Badge>
          </div>
        </Card.Body>
      </Card>

      {/* Messages */}
      {error && <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Tableau des factures */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Chargement des factures...</p>
            </div>
          ) : salesData.length === 0 ? (
            <div className="text-center py-5">
              <Receipt size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Aucune facture trouvée</h5>
              <p className="text-muted">
                {searchTerm || dateFilter !== 'today' || statusFilter
                  ? 'Essayez de modifier vos filtres'
                  : "Aucune vente aujourd'hui"}
              </p>
            </div>
          ) : (
            <>
              {/* ✅ Table Desktop */}
              <div className="table-responsive d-none d-lg-block">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>N° Facture</th>
                      <th>Client</th>
                      <th>Date</th>
                      <th>Montant</th>
                      <th>Paiement</th>
                      <th>Statut</th>
                      <th>Vendeur</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.map((sale) => {
                      const payBadge = getPaymentBadge(sale.paymentMethod);
                      return (
                        <tr key={sale.id}>
                          <td>
                            <code className="text-primary">{sale.saleNumber}</code>
                          </td>
                          <td>
                            <div>
                              <strong>{sale.customer?.name || sale.customerName || 'Client anonyme'}</strong>
                              {(sale.customer?.phone || sale.customerPhone) && (
                                <div className="small text-muted">
                                  <Phone size={12} className="me-1" />
                                  {sale.customer?.phone || sale.customerPhone}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDate(sale.createdAt || sale.saleDate)}
                            </small>
                          </td>
                          <td>
                            <strong className="text-success">{formatCurrency(sale.total)}</strong>
                            {sale.discount > 0 && (
                              <div className="small text-muted">Remise: -{formatCurrency(sale.discount)}</div>
                            )}
                          </td>
                          <td>
                            <Badge bg={payBadge.bg} style={{ fontSize: '0.75rem' }}>
                              {payBadge.icon} {payBadge.label}
                            </Badge>
                          </td>
                          <td>{getStatusBadge(sale)}</td>
                          <td>
                            <small className="text-muted">
                              {sale.seller?.name || sale.sellerName || sale.seller?.firstName || user?.first_name || 'N/A'}
                            </small>
                          </td>
                          <td className="text-center">
                            <div className="btn-group" role="group">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Voir la facture</Tooltip>}>
                                <Button variant="outline-primary" size="sm" onClick={() => handleViewInvoice(sale)}>
                                  <Eye size={14} />
                                </Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Prévisualiser</Tooltip>}>
                                <Button variant="outline-info" size="sm" onClick={() => handlePreviewInvoice(sale)}>
                                  <FileText size={14} />
                                </Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Imprimer</Tooltip>}>
                                <Button variant="outline-secondary" size="sm" onClick={() => handlePrintInvoice(sale)} disabled={printing === sale.id}>
                                  {printing === sale.id ? <Spinner animation="border" size="sm" /> : <Printer size={14} />}
                                </Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Télécharger</Tooltip>}>
                                <Button variant="outline-success" size="sm" onClick={() => handleDownloadInvoice(sale)} disabled={downloading === sale.id}>
                                  {downloading === sale.id ? <Spinner animation="border" size="sm" /> : <Download size={14} />}
                                </Button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* ✅ Cards Mobile */}
              <div className="d-lg-none">
                {salesData.map((sale) => {
                  const payBadge = getPaymentBadge(sale.paymentMethod);
                  return (
                    <Card key={sale.id} className="mb-2 mx-2 shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <code className="bg-light px-2 py-1 rounded" style={{ fontSize: '0.8rem' }}>
                              {sale.saleNumber}
                            </code>
                            <div className="mt-1">
                              {getStatusBadge(sale)}
                              <Badge bg={payBadge.bg} className="ms-1" style={{ fontSize: '0.7rem' }}>
                                {payBadge.icon} {payBadge.label}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="fw-bold text-success" style={{ fontSize: '1rem' }}>
                              {formatCurrency(sale.total)}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <strong style={{ fontSize: '0.85rem' }}>
                              {sale.customer?.name || sale.customerName || 'Client anonyme'}
                            </strong>
                            {(sale.customer?.phone || sale.customerPhone) && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                <Phone size={10} className="me-1" />
                                {sale.customer?.phone || sale.customerPhone}
                              </div>
                            )}
                          </div>
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                            {formatDate(sale.createdAt || sale.saleDate)}
                          </small>
                        </div>

                        {/* Actions mobile */}
                        <div className="d-flex gap-1 pt-2 border-top">
                          <Button variant="outline-primary" size="sm" className="flex-fill" onClick={() => handleViewInvoice(sale)}>
                            <Eye size={14} className="me-1" /> Voir
                          </Button>
                          <Button variant="outline-secondary" size="sm" className="flex-fill" onClick={() => handlePrintInvoice(sale)} disabled={printing === sale.id}>
                            {printing === sale.id ? <Spinner animation="border" size="sm" /> : <><Printer size={14} className="me-1" /> Imprimer</>}
                          </Button>
                          <Button variant="outline-success" size="sm" onClick={() => handleDownloadInvoice(sale)} disabled={downloading === sale.id}>
                            {downloading === sale.id ? <Spinner animation="border" size="sm" /> : <Download size={14} />}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>

              {/* ✅ Pagination — identique à SalesList */}
              {serverPagination.totalPages > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top gap-3">
                  <div className="d-flex align-items-center gap-2 order-2 order-md-1">
                    <small className="text-muted">
                      {(currentPage - 1) * itemsPerPage + 1} à{' '}
                      {Math.min(currentPage * itemsPerPage, serverPagination.totalElements)}{' '}
                      sur {serverPagination.totalElements} factures
                    </small>
                    <span className="text-muted">|</span>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Select
                        size="sm"
                        value={itemsPerPage}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ width: 'auto', minWidth: '60px' }}
                      >
                        {pageSizeOptions.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </Form.Select>
                      <small className="text-muted d-none d-sm-inline">/ page</small>
                    </div>
                  </div>

                  <div className="order-1 order-md-2">
                    {/* Mobile */}
                    <div className="d-md-none">
                      <div className="d-flex align-items-center gap-2">
                        <Button variant="outline-primary" size="sm" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                          ‹ Préc
                        </Button>
                        <span className="px-3 py-1 bg-light rounded">
                          {currentPage} / {serverPagination.totalPages}
                        </span>
                        <Button variant="outline-primary" size="sm" disabled={currentPage === serverPagination.totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                          Suiv ›
                        </Button>
                      </div>
                    </div>

                    {/* Desktop */}
                    <Pagination className="mb-0 d-none d-md-flex">
                      <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                      {(() => {
                        const total = serverPagination.totalPages;
                        const pages = [];
                        if (total <= 5) {
                          for (let i = 1; i <= total; i++) {
                            pages.push(
                              <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                                {i}
                              </Pagination.Item>
                            );
                          }
                        } else {
                          const start = Math.max(1, currentPage - 2);
                          const end = Math.min(total, start + 4);
                          if (start > 1) {
                            pages.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
                            if (start > 2) pages.push(<Pagination.Ellipsis key="e1" disabled />);
                          }
                          for (let i = start; i <= end; i++) {
                            pages.push(
                              <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                                {i}
                              </Pagination.Item>
                            );
                          }
                          if (end < total) {
                            if (end < total - 1) pages.push(<Pagination.Ellipsis key="e2" disabled />);
                            pages.push(<Pagination.Item key={total} onClick={() => handlePageChange(total)}>{total}</Pagination.Item>);
                          }
                        }
                        return pages;
                      })()}
                      <Pagination.Next disabled={currentPage === serverPagination.totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal de prévisualisation */}
      <InvoicePreviewModal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        sale={selectedSale}
        boutique={getBoutiqueData()}
        onPrint={handlePrintInvoice}
        onDownload={handleDownloadInvoice}
      />

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Container>
  );
};

// Composant Modal de prévisualisation de facture (inchangé)
const InvoicePreviewModal = ({ show, onHide, sale, boutique, onPrint, onDownload }) => {
  if (!sale) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F CFA';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Receipt size={24} className="me-2" />
          Facture #{sale.saleNumber}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="invoice-preview" style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.4', color: '#333' }}>
          {/* En-tête boutique */}
          <div className="text-center mb-4 pb-3 border-bottom">
            <div className="d-flex justify-content-center align-items-center mb-2">
              {boutique?.logoUrl ? (
                <div className="me-3">
                  <img
                    src={boutique.logoUrl}
                    alt={boutique.name}
                    style={{ height: '60px', width: '60px', objectFit: 'cover', borderRadius: '8px', border: `2px solid ${boutique?.primaryColor || '#0d6efd'}` }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                  />
                  <div
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{ height: '60px', width: '60px', background: `linear-gradient(135deg, ${boutique?.primaryColor || '#0d6efd'}, ${boutique?.secondaryColor || '#6c757d'})`, color: 'white', borderRadius: '8px', fontSize: '24px', fontWeight: 'bold', display: 'none' }}
                  >
                    {(boutique?.name || 'S').charAt(0)}
                  </div>
                </div>
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center me-3"
                  style={{ height: '60px', width: '60px', background: `linear-gradient(135deg, ${boutique?.primaryColor || '#0d6efd'}, ${boutique?.secondaryColor || '#6c757d'})`, color: 'white', borderRadius: '8px', fontSize: '24px', fontWeight: 'bold' }}
                >
                  {(boutique?.name || 'S').charAt(0)}
                </div>
              )}
              <div>
                <h3 className="mb-1" style={{ color: boutique?.primaryColor || '#0d6efd', fontWeight: 'bold' }}>
                  {boutique?.name || 'SmartBoutik'}
                </h3>
                <p className="mb-0 text-muted small">{boutique?.address || 'Adresse non spécifiée'}</p>
                {boutique?.phone && (
                  <p className="mb-0 text-muted small"><Phone size={12} className="me-1" />{boutique.phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Infos facture */}
          <Row className="mb-4">
            <Col md={6}>
              <h6 className="text-primary mb-2">Informations client</h6>
              <div><strong>{sale.customer?.name || sale.customerName || 'Client anonyme'}</strong></div>
              {(sale.customer?.phone || sale.customerPhone) && (
                <div className="text-muted small"><Phone size={12} className="me-1" />{sale.customer?.phone || sale.customerPhone}</div>
              )}
              {(sale.customer?.address || sale.customerAddress) && (
                <div className="text-muted small"><Building size={12} className="me-1" />{sale.customer?.address || sale.customerAddress}</div>
              )}
            </Col>
            <Col md={6} className="text-end">
              <h6 className="text-primary mb-2">Détails facture</h6>
              <div><strong>N° Facture:</strong> {sale.saleNumber}</div>
              <div><strong>Date:</strong> {formatDate(sale.createdAt || sale.saleDate)}</div>
              <div><strong>Vendeur:</strong> {sale.seller?.name || sale.sellerName || 'N/A'}</div>
            </Col>
          </Row>

          {/* Articles */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Articles vendus</h6>
            <Table size="sm" className="border">
              <thead style={{ backgroundColor: (boutique?.primaryColor || '#0d6efd') + '15' }}>
                <tr>
                  <th>Article</th>
                  <th className="text-center">Quantité</th>
                  <th className="text-end">Prix unitaire</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {(sale.items || sale.saleItems || []).map((item, index) => (
                  <tr key={index}>
                    <td>{item.productName || item.product_name || item.name}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-end">{formatCurrency(item.unitPrice || item.unit_price || item.price)}</td>
                    <td className="text-end">{formatCurrency((item.unitPrice || item.unit_price || item.price) * item.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Totaux */}
          <Row>
            <Col md={6}>
              {sale.notes && (
                <div>
                  <h6 className="text-primary">Notes</h6>
                  <p className="small text-muted">{sale.notes}</p>
                </div>
              )}
            </Col>
            <Col md={6}>
              <div className="border-start ps-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Sous-total:</span>
                  <span>{formatCurrency((sale.total || 0) + (sale.discount || 0))}</span>
                </div>
                {sale.discount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-danger">
                    <span>Remise:</span>
                    <span>-{formatCurrency(sale.discount)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2 border-top pt-2">
                  <strong>Total à payer:</strong>
                  <strong style={{ color: boutique?.primaryColor || '#0d6efd' }}>{formatCurrency(sale.total)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Mode de paiement:</span>
                  <Badge bg={sale.isCredit ? 'warning' : 'success'}>
                    {sale.isCredit ? 'À crédit' : (sale.paymentMethod || 'Espèces')}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
        <Button variant="primary" onClick={() => onPrint(sale)}>
          <Printer size={16} className="me-2" />Imprimer
        </Button>
        <Button variant="success" onClick={() => onDownload(sale)}>
          <Download size={16} className="me-2" />Télécharger
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceManagement;